import { useState, useEffect } from "react";
import "../Modal.css";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import heic2any from "heic2any";

const ModalAddNotes = ({ isVisible, onClose, orderId, products }) => {
  const [images, setImages] = useState([]); // For multiple images
  const [formData, setFormData] = useState({
    projectNotes: "",
  });

  // Only set initial values when modal opens
  useEffect(() => {
    if (isVisible) {
      setFormData({
        projectNotes: products?.projectNotes || "",
      });
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      // Ensure products.images is an array of valid File/Blob objects
      if (Array.isArray(products.images)) {
        setImages(products.images);
      } else {
        setImages([]); // Reset if invalid
      }
    }
  }, [isVisible]);

  // Handle input change
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append("projectNotes", formData.projectNotes);

    // Append each image to the FormData
    images.forEach((image, index) => {
      data.append("images", image);
    });

    try {
      const response = await fetch(
        `https://server-gs.vercel.app/admin/order-notes-edit/${orderId}`,
        {
          method: "PUT",
          body: data, // No need to set Content-Type header, browser will handle it
        }
      );

      const result = await response.json();
      if (response.ok) {
        Swal.fire({
          title: "Success!",
          text: "Product notes added successfully.",
          icon: "success",
          timer: 1500, // Auto-close after 1.5 seconds
          showConfirmButton: false, // No "OK" button
        });
        onClose(); // Close modal on success
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Failed to update order.");
    }
  };

  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingImages, setIsDraggingImages] = useState(false);
  const [isLoadingSingleImage, setIsLoadingSingleImage] = useState(false); // Loading state for single image
  const [isLoadingImages, setIsLoadingImages] = useState(false); // Loading state for multiple images

  // Handle file processing (single or multiple)
  const handleFile = async (file, setIsLoading, setImageState) => {
    setIsLoading(true); // Start loading

    try {
      // Check if the file is HEIC/HEIF
      if (
        file.name.toLowerCase().endsWith(".heic") ||
        file.name.toLowerCase().endsWith(".heif")
      ) {
        // Convert HEIC to JPEG
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8, // Adjust quality as needed
        });

        // Create a new File object with the converted blob
        const convertedFile = new File(
          [convertedBlob],
          file.name.replace(/\.heic$|\.heif$/i, ".jpeg"),
          {
            type: "image/jpeg",
          }
        );

        setImageState(convertedFile);
      } else {
        // If it's not HEIC, use the file as-is
        setImageState(file);
      }
    } catch (error) {
      console.error("Error processing file:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  // Handle file selection for multiple images
  const handleImageChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsLoadingImages(true); // Start loading

      const files = Array.from(e.target.files);
      const convertedFiles = await Promise.all(
        files.map(async (file) => {
          if (
            file.name.toLowerCase().endsWith(".heic") ||
            file.name.toLowerCase().endsWith(".heif")
          ) {
            // Convert HEIC to JPEG
            const convertedBlob = await heic2any({
              blob: file,
              toType: "image/jpeg",
              quality: 0.8, // Adjust quality as needed
            });
            return new File(
              [convertedBlob],
              file.name.replace(/\.heic$|\.heif$/i, ".jpeg"),
              {
                type: "image/jpeg",
              }
            );
          }
          return file;
        })
      );

      setImages((prevImages) => [...prevImages, ...convertedFiles]);
      setIsLoadingImages(false); // Stop loading
    }
  };

  // Handle file drop for multiple images
  const handleDropImages = async (e) => {
    e.preventDefault();
    setIsDraggingImages(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setIsLoadingImages(true); // Start loading

      const files = Array.from(e.dataTransfer.files);
      const convertedFiles = await Promise.all(
        files.map(async (file) => {
          if (
            file.name.toLowerCase().endsWith(".heic") ||
            file.name.toLowerCase().endsWith(".heif")
          ) {
            // Convert HEIC to JPEG
            const convertedBlob = await heic2any({
              blob: file,
              toType: "image/jpeg",
              quality: 0.8, // Adjust quality as needed
            });
            return new File(
              [convertedBlob],
              file.name.replace(/\.heic$|\.heif$/i, ".jpeg"),
              {
                type: "image/jpeg",
              }
            );
          }
          return file;
        })
      );

      setImages((prevImages) => [...prevImages, ...convertedFiles]);
      setIsLoadingImages(false); // Stop loading
    }
  };

  // Handle drag over for multiple images
  const handleDragOverImages = (e) => {
    e.preventDefault();
    setIsDraggingImages(true);
  };

  // Handle drag leave for multiple images
  const handleDragLeaveImages = () => {
    setIsDraggingImages(false);
  };

  // Remove an image from the multiple images list
  const removeImage = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 flex justify-center items-center z-50">
      <div className="bg-gray-100 rounded-2xl shadow-2xl w-[700px] mx-5 sm:mx-auto overflow-hidden">
        {/* Header Section */}
        <div className="flex justify-between items-center py-4 px-6 bg-blue-600">
          <h1 className="text-white text-xl font-semibold font-roboto">
            Project Notes
          </h1>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-blue-700 transition-colors duration-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form Section */}
        <div className="overflow-y-auto">
          <form onSubmit={handleSubmit} className="">
            <div className="p-6 space-y-6">
              {/* Project Notes */}
              <div>
                <label className="block font-roboto text-md text-gray-900 mb-2">
                  Project Notes
                </label>
                <textarea
                  type="text"
                  name="projectNotes"
                  placeholder="Your project Notes"
                  value={formData.projectNotes}
                  onChange={handleChange}
                  className="w-full px-4 py-2 h-36 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                />
              </div>

              {/* Image Upload Section */}
              <div className="flex flex-col">
                <label className="block font-roboto text-md text-gray-900 mb-2">
                  Upload Multiple Images
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDraggingImages
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300 bg-white"
                  }`}
                  onDragOver={handleDragOverImages}
                  onDragLeave={handleDragLeaveImages}
                  onDrop={handleDropImages}
                >
                  {isLoadingImages ? (
                    <div className="flex flex-col items-center">
                      {/* Spinning Loader */}
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-gray-500">Processing images...</p>
                    </div>
                  ) : images.length > 0 ? (
                    <div className="grid grid-cols-3 gap-4">
                      {images.map((image, index) => (
                        <div key={index} className="relative">
                          {/* Check if image is a File/Blob or a URL */}
                          {image instanceof File || image instanceof Blob ? (
                            <img
                              src={URL.createObjectURL(image)}
                              alt={`preview-${index}`}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                          ) : (
                            <img
                              src={image}
                              alt={`preview-${index}`}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white rounded-full"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">
                      Drag & drop files here or click to select
                    </p>
                  )}
                  <input
                    type="file"
                    id="fileInputImage"
                    className="hidden"
                    multiple
                    accept="image/*, .heic, .heif"
                    onChange={handleImageChange}
                  />
                  <label
                    htmlFor="fileInputImage"
                    className="mt-2 inline-block text-blue-600 underline cursor-pointer"
                  >
                    Browse Files
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="p-6 flex justify-start bg-gray-50 border-t border-gray-200">
              <button
                type="submit"
                className="w-56 py-3 font-roboto  bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg focus:ring-4 focus:outline-none focus:ring-blue-300 transition-all"
              >
                Update
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ModalAddNotes;
